// Excel-style per-column filtering for the engagement tracker grid.
//
// A column filter is stored per colKey ("n_<key>" | "f_<fieldId>") as:
//   { selected: [displayValue] | null, condition: { op, v1, v2 } | null }
// `selected` is the value-checklist ("(Select All)" state = null = no filter);
// `condition` is a typed rule from the Text/Number/Date Filters submenu.
// Both can be active at once — a row must pass both.

// Sentinel shown in the checklist for rows with no value in the column.
export const BLANKS = "(Blanks)";

export function isActiveColumnFilter(f) {
	if (!f) return false;
	if (Array.isArray(f.selected)) return true;
	if (f.condition && f.condition.op) return true;
	return false;
}

export function activeColumnFilterCount(filters) {
	return Object.keys(filters || {}).filter(k => isActiveColumnFilter(filters[k])).length;
}

// ---------- Column filter type ----------

/**
 * Resolve the filter type for a column descriptor: "text" | "number" | "date" |
 * "boolean". Native columns carry an explicit type; custom text columns are
 * sniffed against the loaded rows — if every non-empty value is numeric the
 * column gets Number Filters (like Excel).
 */
export function columnFilterType(d, rows) {
	if (d.kind === "native") {
		if (d.col.type === "date") return "date";
		if (d.col.type === "checkbox") return "boolean";
		return "text";
	}
	const f = d.field;
	const derived = f.derived_from && f.derived_from.type;
	if (derived === "date") return "date";
	if (derived === "checkbox" || f.field_type === "checkbox") return "boolean";
	if (f.field_type === "dropdown") return "text";
	let sawNumber = false;
	for (const row of rows || []) {
		const v = row.values ? row.values[f.id] : null;
		if (v == null || v === "") continue;
		if (isNaN(Number(v))) return "text";
		sawNumber = true;
	}
	return sawNumber ? "number" : "text";
}

// ---------- Cell value extraction ----------

export function formatDateValue(iso) {
	if (!iso) return "";
	if (typeof iso === "string" && iso.length >= 10) return iso.substr(0, 10);
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return "";
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");
		return y + "-" + m + "-" + day;
	} catch (e) {
		return "";
	}
}

export function formatDateDisplay(iso) {
	const v = formatDateValue(iso);
	if (!v) return "";
	const [y, m, d] = v.split("-");
	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	const mi = parseInt(m, 10) - 1;
	if (mi < 0 || mi > 11) return v;
	return months[mi] + " " + parseInt(d, 10) + ", " + y;
}

/**
 * The display string a column shows for a row — this is what the checklist
 * lists and what text conditions match against. "" means blank.
 */
export function cellFilterValue(row, d, grid) {
	if (d.kind === "custom") {
		const f = d.field;
		const raw = row.values ? row.values[f.id] : null;
		if (f.field_type === "checkbox" || (f.derived_from && f.derived_from.type === "checkbox")) {
			return raw === "1" || raw === 1 || raw === true ? "Yes" : "No";
		}
		if (raw == null || raw === "") return "";
		if (f.derived_from && f.derived_from.type === "date") return formatDateDisplay(raw);
		const dsl = grid.datasource_labels && grid.datasource_labels[f.id];
		if (dsl && dsl[raw] != null) return String(dsl[raw]);
		if (f.field_type === "dropdown" && Array.isArray(f.options)) {
			const o = f.options.find(x => x.key === raw);
			if (o) return o.label || o.key;
		}
		return String(raw);
	}
	const key = d.col.key;
	const native = row.native || {};
	switch (key) {
		case "name":
			return row.engagement_name || "";
		case "client_id":
			return row.client_name || "";
		case "flow_id": {
			const f = (grid.flows || []).find(x => x.id === native.flow_id);
			return f ? f.name || "" : "";
		}
		case "flow_state_id":
			return native.flow_state ? native.flow_state.name || "" : "";
		case "team_id": {
			const t = (grid.teams || []).find(x => x.id === native.team_id);
			return t ? t.name || "" : "";
		}
		case "tax_estimate":
			return native.tax_estimate ? "Yes" : "No";
		case "due_date":
		case "date_in":
		case "fiscal_year_end":
			return formatDateDisplay(native[key]);
		default:
			return native[key] != null ? String(native[key]) : "";
	}
}

// The ISO (YYYY-MM-DD) value for a date column's cell — used by date
// conditions so they compare real dates, not display strings.
export function cellDateIso(row, d) {
	if (d.kind === "custom") {
		const raw = row.values ? row.values[d.field.id] : null;
		return formatDateValue(raw);
	}
	const native = row.native || {};
	return formatDateValue(native[d.col.key]);
}

// ---------- Condition operators ----------

export const CONDITION_OPS = {
	text: [
		{ op: "eq", label: "Equals…" },
		{ op: "neq", label: "Does Not Equal…" },
		{ op: "contains", label: "Contains…" },
		{ op: "notContains", label: "Does Not Contain…" },
		{ op: "beginsWith", label: "Begins With…" },
		{ op: "endsWith", label: "Ends With…" },
	],
	number: [
		{ op: "eq", label: "Equals…" },
		{ op: "neq", label: "Does Not Equal…" },
		{ op: "gt", label: "Greater Than…" },
		{ op: "gte", label: "Greater Than Or Equal To…" },
		{ op: "lt", label: "Less Than…" },
		{ op: "lte", label: "Less Than Or Equal To…" },
		{ op: "between", label: "Between…", two: true },
	],
	date: [
		{ op: "eq", label: "Equals…" },
		{ op: "before", label: "Before…" },
		{ op: "after", label: "After…" },
		{ op: "between", label: "Between…", two: true },
	],
	boolean: [],
};

export function conditionSummary(type, cond) {
	if (!cond || !cond.op) return "";
	const def = (CONDITION_OPS[type] || []).find(o => o.op === cond.op);
	const label = def ? def.label.replace("…", "") : cond.op;
	if (def && def.two) return label + " " + (cond.v1 || "?") + " and " + (cond.v2 || "?");
	return label + ' "' + (cond.v1 || "") + '"';
}

/**
 * Evaluate a condition against one row's cell. Blank cells never match a
 * condition (Excel behavior). `display` is the cell's display string; `iso`
 * is the YYYY-MM-DD value for date columns.
 */
export function evalCondition(type, cond, display, iso) {
	if (!cond || !cond.op) return true;

	if (type === "date") {
		if (!iso) return false;
		const v = Date.parse(iso);
		const a = cond.v1 ? Date.parse(cond.v1) : NaN;
		const b = cond.v2 ? Date.parse(cond.v2) : NaN;
		if (isNaN(v)) return false;
		switch (cond.op) {
			case "eq":
				return !isNaN(a) && v === a;
			case "before":
				return !isNaN(a) && v < a;
			case "after":
				return !isNaN(a) && v > a;
			case "between":
				return !isNaN(a) && !isNaN(b) && v >= Math.min(a, b) && v <= Math.max(a, b);
			default:
				return true;
		}
	}

	if (type === "number") {
		if (display === "") return false;
		const v = Number(display);
		const a = Number(cond.v1);
		const b = Number(cond.v2);
		if (isNaN(v)) return false;
		switch (cond.op) {
			case "eq":
				return v === a;
			case "neq":
				return v !== a;
			case "gt":
				return v > a;
			case "gte":
				return v >= a;
			case "lt":
				return v < a;
			case "lte":
				return v <= a;
			case "between":
				return !isNaN(a) && !isNaN(b) && v >= Math.min(a, b) && v <= Math.max(a, b);
			default:
				return true;
		}
	}

	const hay = (display || "").toLowerCase();
	const needle = (cond.v1 || "").toLowerCase();
	if (display === "") return false;
	switch (cond.op) {
		case "eq":
			return hay === needle;
		case "neq":
			return hay !== needle;
		case "contains":
			return hay.indexOf(needle) !== -1;
		case "notContains":
			return hay.indexOf(needle) === -1;
		case "beginsWith":
			return hay.indexOf(needle) === 0;
		case "endsWith":
			return needle.length <= hay.length && hay.lastIndexOf(needle) === hay.length - needle.length;
		default:
			return true;
	}
}

/**
 * Does a row pass one column's filter? A filter is EITHER a condition or a
 * value checklist (Excel behavior — the two are alternative modes, so a
 * condition takes precedence over any checklist that may also be stored).
 */
export function rowPassesColumnFilter(row, d, filter, grid, type) {
	if (!isActiveColumnFilter(filter)) return true;
	const display = cellFilterValue(row, d, grid);

	if (filter.condition && filter.condition.op) {
		const iso = type === "date" ? cellDateIso(row, d) : null;
		return evalCondition(type, filter.condition, display, iso);
	}

	if (Array.isArray(filter.selected)) {
		const token = display === "" ? BLANKS : display;
		if (filter.selected.indexOf(token) === -1) return false;
	}
	return true;
}

/**
 * Sort the checklist's distinct values naturally for the column type:
 * numbers numerically, dates chronologically, text alphabetically. Blanks
 * are handled by the caller (always appended last).
 */
export function sortDistinctValues(values, type) {
	const list = values.slice();
	if (type === "number") {
		list.sort((a, b) => Number(a.value) - Number(b.value));
	} else if (type === "date") {
		list.sort((a, b) => {
			const ta = Date.parse(a.iso || "");
			const tb = Date.parse(b.iso || "");
			if (isNaN(ta) && isNaN(tb)) return 0;
			if (isNaN(ta)) return 1;
			if (isNaN(tb)) return -1;
			return ta - tb;
		});
	} else {
		list.sort((a, b) => String(a.value).localeCompare(String(b.value)));
	}
	return list;
}
